import { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiError } from "@/lib/kfmApi";
import { Loader2, Clapperboard, Sparkles, Trash2, Send, Gauge, Facebook, Play, Upload, Pencil, Check, X, Mic, Clapperboard as SceneIcon, UserRound, Archive, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useHub, olderThan, matchesSearch } from "@/context/contentHub";

const ALL_PLATFORMS = ["instagram", "tiktok", "facebook", "youtube"];
const STATUS_STYLES = {
  draft: "bg-sky-500/10 text-sky-400",
  generating: "bg-amber-500/10 text-amber-400",
  ready: "bg-[#10222A] text-[#00E5FF]",
  submitted: "bg-emerald-500/10 text-emerald-400",
  dry_run: "bg-[#141B29] text-slate-400",
  failed: "bg-red-500/10 text-red-400",
};

const REEL_TYPES = [
  { id: "broll", label: "B-roll + voiceover", icon: Clapperboard, cost: "~$0.70 / reel", hint: "Cinematic footage with your chosen narration voice over it." },
  { id: "avatar", label: "Talking Avatar", icon: UserRound, cost: "~$0.30–0.60 / reel", hint: "A person (uploaded or AI-generated) lip-synced to your chosen voice." },
  { id: "scene", label: "Talking Scene", icon: SceneIcon, cost: "~$0.60–1.50 / reel", hint: "AI generates people speaking with native synced audio (voice picker doesn't apply)." },
];

function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return "—"; }
}

export default function ReelsAdmin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [brief, setBrief] = useState("");
  const [platforms, setPlatforms] = useState(["facebook"]);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [capInput, setCapInput] = useState("");
  const [savingCap, setSavingCap] = useState(false);

  // Voice picker + reel type
  const [voiceCfg, setVoiceCfg] = useState({ voices: [], deliveries: [], default_voice: "", default_delivery: "balanced" });
  const [reelType, setReelType] = useState("broll");
  const [voiceId, setVoiceId] = useState("");
  const [delivery, setDelivery] = useState("balanced");
  const [duration, setDuration] = useState(10);
  const [previewing, setPreviewing] = useState(false);
  const [avatar, setAvatar] = useState(null); // {image_id, image_url}
  const [uploading, setUploading] = useState(false);
  const [settingDefault, setSettingDefault] = useState(false);

  // Caption editing
  const [editId, setEditId] = useState("");
  const [captionDraft, setCaptionDraft] = useState("");
  const [savingCap2, setSavingCap2] = useState(false);
  const [draftEdit, setDraftEdit] = useState({}); // reelId -> {video_prompt, script}
  const [showArchive, setShowArchive] = useState(false);
  const { search: hubSearch = "", filter: hubFilter = "all", archiveDays = 7, reportSummary } = useHub();

  const pollRef = useRef(null);
  const audioRef = useRef(null);
  const fileRef = useRef(null);
  const defFileRef = useRef(null);
  const initedRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/reels");
      setData(data);
      setCapInput(String(data?.daily_cap ?? ""));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load reels");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!reportSummary || !data) return;
    const list = data?.reels || [];
    const c = {};
    list.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    const dots = [];
    if (c.draft) dots.push({ label: `${c.draft} draft`, tone: "amber" });
    if (c.failed) dots.push({ label: `${c.failed} failed`, tone: "red" });
    if (c.generating) dots.push({ label: `${c.generating} rendering`, tone: "blue" });
    if (c.ready) dots.push({ label: `${c.ready} ready`, tone: "blue" });
    if (c.submitted) dots.push({ label: `${c.submitted} posted`, tone: "green" });
    const needsAction = (c.draft || 0) + (c.failed || 0) + (c.ready || 0);
    reportSummary({ subtitle: `${list.length} total · ${needsAction} need you`, needsAction, dots });
  }, [data, reportSummary]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/reels/voices");
        setVoiceCfg(data);
      } catch { /* picker just stays empty if it fails */ }
    })();
  }, []);

  // Initialize the picker from the owner's SAVED defaults (what Shee mirrors), once both loads land.
  useEffect(() => {
    if (initedRef.current) return;
    if (!(voiceCfg.voices || []).length || !data) return;
    const cd = data.creator_defaults || {};
    setVoiceId(cd.voice_id || voiceCfg.default_voice);
    setDelivery(cd.delivery || voiceCfg.default_delivery || "balanced");
    setReelType(cd.reel_type || "broll");
    setDuration(cd.duration || voiceCfg.default_duration || 10);
    initedRef.current = true;
  }, [voiceCfg, data]);

  // Persist picker changes so Shee uses the SAME voice / mood / type / length the owner selected here.
  const persistDefaults = (patch) => { api.put("/admin/reels/creator-defaults", patch).catch(() => {}); };
  const chooseVoice = (v) => { setVoiceId(v); persistDefaults({ voice_id: v }); };
  const chooseDelivery = (d) => { setDelivery(d); persistDefaults({ delivery: d }); };
  const chooseType = (t) => { setReelType(t); persistDefaults({ reel_type: t }); };
  const chooseDuration = (d) => { setDuration(d); persistDefaults({ duration: d }); };

  useEffect(() => {
    const generating = (data?.reels || []).some((r) => r.status === "generating");
    if (generating && !pollRef.current) {
      pollRef.current = setInterval(load, 6000);
    } else if (!generating && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [data, load]);

  const togglePlat = (p) =>
    setPlatforms((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));

  const preview = async () => {
    if (!voiceId) return;
    setPreviewing(true);
    try {
      const { data } = await api.post("/admin/reels/preview-voice", { voice_id: voiceId, delivery });
      if (audioRef.current) { audioRef.current.pause(); }
      const a = new Audio(data.audio_url);
      audioRef.current = a;
      await a.play();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't preview that voice");
    }
    setPreviewing(false);
  };

  const uploadAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/reels/avatar-image", fd);
      setAvatar(data);
      toast.success("Avatar image ready");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't upload that image");
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const uploadDefaultAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setSettingDefault(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data: up } = await api.post("/admin/reels/avatar-image", fd);
      await api.put("/admin/reels/default-avatar", { image_id: up.image_id });
      toast.success("Shee's default avatar saved");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't set the default avatar");
    }
    setSettingDefault(false);
    if (defFileRef.current) defFileRef.current.value = "";
  };

  const clearDefaultAvatar = async () => {
    setSettingDefault(true);
    try {
      await api.put("/admin/reels/default-avatar", { image_id: null });
      toast.success("Default avatar cleared — Shee will auto-generate a presenter");
      await load();
    } catch {
      toast.error("Couldn't clear the default avatar");
    }
    setSettingDefault(false);
  };

  const create = async () => {
    if (!brief.trim()) { toast.error("Give a brief or theme for the reel"); return; }
    setCreating(true);
    try {
      await api.post("/admin/reels/draft", {
        brief: brief.trim(), platforms, reel_type: reelType, delivery, duration,
        voice_id: reelType === "scene" ? null : voiceId,
        image_id: reelType === "avatar" ? avatar?.image_id || null : null,
      });
      toast.success("Draft ready — review the prompt & script below, then Generate.");
      setBrief("");
      setAvatar(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't draft the reel");
    }
    setCreating(false);
  };

  const setDf = (id, key, val) => setDraftEdit((d) => ({ ...d, [id]: { ...d[id], [key]: val } }));
  const dfVal = (r, key) => (draftEdit[r.id]?.[key] ?? (r[key] || ""));

  const render = async (id) => {
    if (!window.confirm("Generate this video now? This runs a paid render (~1–3 min).")) return;
    setBusyId(id);
    try {
      if (draftEdit[id]) { await api.patch(`/admin/reels/${id}`, draftEdit[id]); }  // save edits first
      await api.post(`/admin/reels/${id}/render`);
      setDraftEdit((d) => { const n = { ...d }; delete n[id]; return n; });
      toast.success("Generating video… it'll appear here when ready.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't generate the video");
    }
    setBusyId("");
  };

  const saveDraft = async (id) => {
    if (!draftEdit[id]) return;
    setBusyId(id);
    try {
      await api.patch(`/admin/reels/${id}`, draftEdit[id]);
      setDraftEdit((d) => { const n = { ...d }; delete n[id]; return n; });
      toast.success("Draft saved");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save the draft");
    }
    setBusyId("");
  };

  const approve = async (id) => {
    if (!window.confirm("Send this reel to PostPulse for its platforms?")) return;
    setBusyId(id);
    try {
      const { data: r } = await api.post(`/admin/reels/${id}/approve`);
      toast.success(r.status_detail || "Sent");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't approve");
    }
    setBusyId("");
  };

  const postFacebook = async (id) => {
    if (!window.confirm("Publish this reel directly to your Facebook Page now? (Near-instant, no PostPulse)")) return;
    setBusyId(id);
    try {
      const { data: r } = await api.post(`/admin/reels/${id}/post-facebook`);
      toast.success(r.status_detail || "Publishing to Facebook…");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't post to Facebook");
    }
    setBusyId("");
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this reel?")) return;
    setBusyId(id);
    try {
      await api.delete(`/admin/reels/${id}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't delete");
    }
    setBusyId("");
  };

  const startEdit = (r) => { setEditId(r.id); setCaptionDraft(r.caption ?? r.script ?? ""); };
  const cancelEdit = () => { setEditId(""); setCaptionDraft(""); };
  const saveCaption = async (id) => {
    setSavingCap2(true);
    try {
      await api.patch(`/admin/reels/${id}`, { caption: captionDraft });
      toast.success("Caption updated");
      cancelEdit();
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save the caption");
    }
    setSavingCap2(false);
  };

  const saveCap = async () => {
    const val = parseInt(capInput, 10);
    if (!val || val < 1) { toast.error("Enter a daily limit of 1 or more"); return; }
    setSavingCap(true);
    try {
      const { data: r } = await api.put("/admin/reels/settings", { daily_cap: val });
      toast.success(`Daily reel limit set to ${r.daily_cap}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save the limit");
    }
    setSavingCap(false);
  };

  if (loading) {
    return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 text-[#00E5FF] animate-spin" strokeWidth={1.5} /></div>;
  }

  const enabled = data?.enabled;
  const reels = data?.reels || [];
  const NEEDS = ["draft", "generating", "ready", "failed"];
  const rMatch = (r) =>
    matchesSearch(`${r.title || ""} ${r.brief || ""} ${r.script || ""} ${r.caption || ""}`, hubSearch) &&
    (hubFilter === "all" ||
      (hubFilter === "action" && ["draft", "failed", "ready"].includes(r.status)) ||
      (hubFilter === "scheduled" && r.status === "generating") ||
      (hubFilter === "posted" && r.status === "submitted"));
  const isArchR = (r) => !NEEDS.includes(r.status) && olderThan(r.rendered_at || r.created_at, archiveDays);
  const shownR = reels.filter(rMatch);
  const activeR = shownR.filter((r) => !isArchR(r));
  const archivedR = shownR.filter(isArchR);
  const reuseBrief = (r) => { setBrief(r.brief || r.title || ""); window.scrollTo({ top: 0, behavior: "smooth" }); toast.success("Brief loaded above — tweak it and draft a fresh reel"); };
  const activeType = REEL_TYPES.find((t) => t.id === reelType);
  const voiceLabel = (id) => (voiceCfg.voices || []).find((v) => v.id === id)?.label || id;
  const deliveryLabel = (id) => (voiceCfg.deliveries || []).find((d) => d.id === id)?.label || id;
  const promptLabel = (rt) => rt === "scene" ? "Scene + spoken dialogue" : rt === "avatar" ? "Presenter description" : "Video prompt";

  // Group voices by their `group` label for the <optgroup>s.
  const grouped = (voiceCfg.voices || []).reduce((acc, v) => {
    (acc[v.group] = acc[v.group] || []).push(v);
    return acc;
  }, {});

  return (
    <div className="space-y-5" data-testid="reels-admin">
      {!enabled && (
        <div className="rounded-xl bg-amber-500/10 text-amber-300 text-sm p-3" data-testid="reels-disabled">
          Video generation isn&rsquo;t configured (missing FAL_KEY). Add it to enable Reels.
        </div>
      )}

      <div className="rounded-2xl bg-[#0E131F] border border-[#1E273D] p-5 space-y-4">
        <div className="flex items-center gap-2 text-slate-200 font-medium"><Clapperboard className="w-5 h-5 text-[#00E5FF]" strokeWidth={1.5} /> New Reel</div>

        {/* Reel type selector */}
        <div className="flex items-stretch gap-2 flex-wrap" data-testid="reel-type-selector">
          {REEL_TYPES.map((t) => {
            const Icon = t.icon;
            const active = reelType === t.id;
            return (
              <button
                key={t.id}
                data-testid={`reel-type-${t.id}`}
                onClick={() => chooseType(t.id)}
                className={`flex-1 min-w-[150px] text-left rounded-xl border p-3 transition-colors ${active ? "border-[#00E5FF] bg-[#10222A]" : "border-[#29344D] bg-[#0E131F] hover:bg-[#0B0F16]"}`}
              >
                <div className="flex items-center gap-2 text-sm font-medium text-slate-200"><Icon className="w-4 h-4 text-[#00E5FF]" strokeWidth={1.5} /> {t.label}</div>
                <div className="text-xs text-slate-500 mt-1">{t.cost}</div>
              </button>
            );
          })}
        </div>
        <p className="text-xs text-slate-500 -mt-1" data-testid="reel-type-hint">{activeType?.hint}</p>

        <textarea
          data-testid="reel-brief"
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          rows={2}
          placeholder="Brief or theme, e.g. 'Hope when life feels overwhelming' — Shee will write the scene + a voiceover."
          className="w-full rounded-xl border border-[#29344D] bg-[#0B0F16] p-3 text-sm text-slate-300 focus:outline-none focus:border-[#00E5FF]/50"
        />

        {/* Voice + delivery picker (B-roll & Avatar) */}
        {reelType !== "scene" ? (
          <div className="rounded-xl border border-[#1E273D] bg-[#0B0F16]/60 p-3 space-y-3" data-testid="voice-picker">
            <div className="flex items-center gap-2 flex-wrap">
              <Mic className="w-4 h-4 text-[#00E5FF]" strokeWidth={1.5} />
              <span className="text-sm text-slate-400">Voice</span>
              <select
                data-testid="voice-select"
                value={voiceId}
                onChange={(e) => chooseVoice(e.target.value)}
                className="flex-1 min-w-[200px] rounded-lg border border-[#29344D] bg-[#0E131F] px-2 py-1.5 text-sm text-slate-300 focus:outline-none focus:border-[#00E5FF]/50"
              >
                {Object.entries(grouped).map(([group, vs]) => (
                  <optgroup key={group} label={group}>
                    {vs.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
                  </optgroup>
                ))}
              </select>
              <button
                data-testid="voice-preview"
                onClick={preview}
                disabled={previewing || !voiceId || !enabled}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#0E131F] border border-[#29344D] hover:border-[#00E5FF] text-slate-400 text-sm px-3 py-1.5 transition-colors disabled:opacity-60"
              >
                {previewing ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <Play className="w-4 h-4" strokeWidth={1.5} />}
                Preview
              </button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-slate-400">Delivery</span>
              {(voiceCfg.deliveries || []).map((d) => (
                <button
                  key={d.id}
                  data-testid={`delivery-${d.id}`}
                  onClick={() => chooseDelivery(d.id)}
                  className={`text-sm px-3 py-1 rounded-full border transition-colors ${delivery === d.id ? "bg-[#00E5FF] text-[#07090E] border-[#00E5FF]" : "bg-[#0E131F] text-slate-400 border-[#29344D] hover:bg-[#0B0F16]"}`}
                >{d.label}</button>
              ))}
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-[#1E273D] bg-[#0B0F16]/60 p-3 flex items-center gap-2 flex-wrap" data-testid="scene-delivery">
            <span className="text-sm text-slate-400">Delivery</span>
            {(voiceCfg.deliveries || []).map((d) => (
              <button
                key={d.id}
                data-testid={`delivery-${d.id}`}
                onClick={() => setDelivery(d.id)}
                className={`text-sm px-3 py-1 rounded-full border transition-colors ${delivery === d.id ? "bg-[#00E5FF] text-[#07090E] border-[#00E5FF]" : "bg-[#0E131F] text-slate-400 border-[#29344D] hover:bg-[#0B0F16]"}`}
              >{d.label}</button>
            ))}
            <span className="text-xs text-slate-500 w-full">Talking Scene uses AI-generated native voices, so the voice picker doesn&rsquo;t apply here.</span>
          </div>
        )}

        {/* Avatar image (Avatar type only) */}
        {reelType === "avatar" && (
          <div className="rounded-xl border border-[#1E273D] bg-[#0B0F16]/60 p-3 flex items-center gap-3 flex-wrap" data-testid="avatar-uploader">
            {avatar?.image_url ? (
              <img src={avatar.image_url} alt="avatar" className="w-14 h-14 rounded-lg object-cover border border-[#29344D]" data-testid="avatar-thumb" />
            ) : (
              <div className="w-14 h-14 rounded-lg bg-[#141B29] flex items-center justify-center text-slate-500"><UserRound className="w-6 h-6" strokeWidth={1.5} /></div>
            )}
            <div className="flex-1 min-w-[180px]">
              <input ref={fileRef} type="file" accept="image/*" onChange={uploadAvatar} className="hidden" data-testid="avatar-file-input" />
              <button
                data-testid="avatar-upload-btn"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#0E131F] border border-[#29344D] hover:border-[#00E5FF] text-slate-400 text-sm px-3 py-1.5 transition-colors disabled:opacity-60"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <Upload className="w-4 h-4" strokeWidth={1.5} />}
                {avatar ? "Replace image" : "Upload face image"}
              </button>
              <p className="text-xs text-slate-500 mt-1">Optional — leave empty and a matching presenter is generated for you.</p>
            </div>
          </div>
        )}

        <div className="rounded-xl border border-[#1E273D] bg-[#0B0F16]/60 p-3 flex items-center gap-2 flex-wrap" data-testid="duration-picker">
          <span className="text-sm text-slate-400">Length</span>
          {(voiceCfg.durations || []).map((d) => (
            <button
              key={d.id}
              data-testid={`duration-${d.id}`}
              onClick={() => chooseDuration(d.id)}
              className={`text-sm px-3 py-1 rounded-full border transition-colors ${duration === d.id ? "bg-[#00E5FF] text-[#07090E] border-[#00E5FF]" : "bg-[#0E131F] text-slate-400 border-[#29344D] hover:bg-[#0B0F16]"}`}
            >{d.label}</button>
          ))}
          {reelType === "scene" && duration > (voiceCfg.scene_max_duration || 12) && (
            <span className="text-xs text-amber-400" data-testid="scene-duration-note">Talking Scene caps at {voiceCfg.scene_max_duration || 12}s — it&rsquo;ll render at {voiceCfg.scene_max_duration || 12}s.</span>
          )}
          {reelType === "broll" && duration > 10 && (
            <span className="text-xs text-slate-500" data-testid="broll-duration-note">B-roll stitches {duration >= 20 ? "two 10s clips" : "a 10s + 5s clip"} (~2× render cost).</span>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {ALL_PLATFORMS.map((p) => (
            <button
              key={p}
              data-testid={`reel-plat-${p}`}
              onClick={() => togglePlat(p)}
              className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${platforms.includes(p) ? "bg-[#00E5FF] text-[#07090E] border-[#00E5FF]" : "bg-[#0E131F] text-slate-400 border-[#29344D] hover:bg-[#0B0F16]"}`}
            >{p.charAt(0).toUpperCase() + p.slice(1)}</button>
          ))}
          <span className="text-xs text-slate-500 ml-auto mr-1">{activeType?.cost}</span>
          <button
            data-testid="reel-generate"
            onClick={create}
            disabled={creating || !enabled}
            className="inline-flex items-center gap-2 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-sm font-medium px-5 py-2 transition-colors disabled:opacity-60"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <Sparkles className="w-4 h-4" strokeWidth={1.5} />}
            Draft &amp; review
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap pt-3 border-t border-[#1E273D]" data-testid="reel-cap-control">
          <Gauge className="w-4 h-4 text-[#00E5FF]" strokeWidth={1.5} />
          <span className="text-sm text-slate-400">Daily generation limit</span>
          <input
            data-testid="reel-cap-input"
            type="number" min={1} max={data?.cap_max || 100}
            value={capInput}
            onChange={(e) => setCapInput(e.target.value)}
            className="w-16 rounded-lg border border-[#29344D] bg-[#0B0F16] px-2 py-1 text-sm text-slate-300 text-center focus:outline-none focus:border-[#00E5FF]/50"
          />
          <button
            data-testid="reel-cap-save"
            onClick={saveCap}
            disabled={savingCap || String(data?.daily_cap ?? "") === capInput.trim()}
            className="text-sm font-medium text-[#00E5FF] hover:text-[#00B8CC] disabled:opacity-40 disabled:cursor-default"
          >{savingCap ? "Saving…" : "Save"}</button>
          <span className="text-xs text-slate-500 ml-auto" data-testid="reel-cap-usage">
            {data?.used_today ?? 0}/{data?.daily_cap ?? 0} used (rolling 24h)
            {data?.used_today >= data?.daily_cap && data?.reset_at ? ` · frees at ${data.reset_at}` : ""}
          </span>
        </div>

        <div className="flex items-center gap-3 flex-wrap pt-3 border-t border-[#1E273D]" data-testid="shee-default-avatar">
          <UserRound className="w-4 h-4 text-[#00E5FF]" strokeWidth={1.5} />
          <span className="text-sm text-slate-400">Shee&rsquo;s default avatar face</span>
          {data?.default_avatar_url ? (
            <img src={data.default_avatar_url} alt="default avatar" className="w-10 h-10 rounded-lg object-cover border border-[#29344D]" data-testid="shee-default-avatar-thumb" />
          ) : (
            <span className="text-xs text-slate-500">None — Shee generates a presenter for avatar reels</span>
          )}
          <input ref={defFileRef} type="file" accept="image/*" onChange={uploadDefaultAvatar} className="hidden" data-testid="shee-default-avatar-input" />
          <button
            data-testid="shee-default-avatar-set"
            onClick={() => defFileRef.current?.click()}
            disabled={settingDefault}
            className="text-sm font-medium text-[#00E5FF] hover:text-[#00B8CC] disabled:opacity-40 inline-flex items-center gap-1"
          >
            {settingDefault ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <Upload className="w-4 h-4" strokeWidth={1.5} />}
            {data?.default_avatar_url ? "Replace" : "Upload & set"}
          </button>
          {data?.default_avatar_url && (
            <button
              data-testid="shee-default-avatar-clear"
              onClick={clearDefaultAvatar}
              disabled={settingDefault}
              className="text-sm text-slate-500 hover:text-red-500 disabled:opacity-40"
            >Clear</button>
          )}
          <span className="text-xs text-slate-500 w-full">When you ask Shee for a Talking Avatar reel, she uses this face; if none is set she generates a matching presenter.</span>
        </div>
      </div>

      {shownR.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#29344D] py-12 text-center text-sm text-slate-500" data-testid="reels-empty">
          {reels.length === 0
            ? "No reels yet. Write a brief above, or turn a Daily Nugget into a Reel."
            : "Nothing matches your search or filter."}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {activeR.map((r) => (
            <div key={r.id} data-testid={`reel-${r.id}`} className="rounded-2xl bg-[#0E131F] border border-[#1E273D] p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-slate-200 truncate">{r.title || r.brief}</span>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full shrink-0 ${STATUS_STYLES[r.status] || STATUS_STYLES.generating}`}>{r.status}</span>
              </div>

              {r.status === "generating" && (
                <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> Generating {r.reel_type === "scene" ? "talking scene" : r.reel_type === "avatar" ? "talking avatar" : "video + voiceover"}…
                </div>
              )}
              {r.status === "failed" && (
                <p className="text-sm text-red-500 py-4">{r.error || r.status_detail || "Generation failed."}</p>
              )}

              {r.status === "draft" && (
                <div className="space-y-2" data-testid={`reel-draft-${r.id}`}>
                  <div className="flex items-center gap-1.5 flex-wrap text-[11px] text-slate-500">
                    <span className="px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-400">{REEL_TYPES.find((t) => t.id === r.reel_type)?.label || r.reel_type}</span>
                    {r.reel_type !== "scene" && <span className="px-2 py-0.5 rounded-full bg-[#141B29]">{voiceLabel(r.voice_id)}</span>}
                    <span className="px-2 py-0.5 rounded-full bg-[#141B29]">{deliveryLabel(r.delivery)}</span>
                    {r.duration && <span className="px-2 py-0.5 rounded-full bg-[#141B29]">{r.reel_type === "scene" ? Math.min(r.duration, voiceCfg.scene_max_duration || 12) : r.duration}s</span>}
                  </div>
                  {(r.reel_type !== "avatar" || (draftEdit[r.id]?.video_prompt ?? r.video_prompt)) && (
                    <div>
                      <label className="text-[11px] uppercase tracking-widest text-[#00E5FF]">{promptLabel(r.reel_type)}</label>
                      <textarea
                        data-testid={`reel-draft-prompt-${r.id}`}
                        value={dfVal(r, "video_prompt")}
                        onChange={(e) => setDf(r.id, "video_prompt", e.target.value)}
                        rows={3}
                        className="w-full rounded-xl border border-[#29344D] bg-[#0B0F16] p-2.5 text-sm text-slate-300 focus:outline-none focus:border-[#00E5FF]/50"
                      />
                    </div>
                  )}
                  <div>
                    <label className="text-[11px] uppercase tracking-widest text-[#00E5FF]">Spoken script <span className="normal-case tracking-normal text-slate-500">— includes a CTA; edit or remove it</span></label>
                    <textarea
                      data-testid={`reel-draft-script-${r.id}`}
                      value={dfVal(r, "script")}
                      onChange={(e) => setDf(r.id, "script", e.target.value)}
                      rows={3}
                      className="w-full rounded-xl border border-[#29344D] bg-[#0B0F16] p-2.5 text-sm text-slate-300 focus:outline-none focus:border-[#00E5FF]/50"
                    />
                  </div>
                </div>
              )}

              {r.video_url && (
                <video
                  data-testid={`reel-video-${r.id}`}
                  src={r.video_url}
                  controls
                  playsInline
                  className="w-full rounded-xl bg-black max-h-[420px]"
                />
              )}

              {r.status !== "draft" && (editId === r.id ? (
                <div className="space-y-2" data-testid={`reel-caption-edit-${r.id}`}>
                  <textarea
                    data-testid={`reel-caption-input-${r.id}`}
                    value={captionDraft}
                    onChange={(e) => setCaptionDraft(e.target.value)}
                    rows={3}
                    className="w-full rounded-xl border border-[#29344D] bg-[#0B0F16] p-2.5 text-sm text-slate-300 focus:outline-none focus:border-[#00E5FF]/50"
                  />
                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`reel-caption-save-${r.id}`}
                      onClick={() => saveCaption(r.id)}
                      disabled={savingCap2}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-sm px-4 py-1.5 transition-colors disabled:opacity-60"
                    >{savingCap2 ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <Check className="w-4 h-4" strokeWidth={1.5} />} Save</button>
                    <button
                      data-testid={`reel-caption-cancel-${r.id}`}
                      onClick={cancelEdit}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#0E131F] border border-[#29344D] text-slate-400 text-sm px-4 py-1.5 hover:bg-[#0B0F16]"
                    ><X className="w-4 h-4" strokeWidth={1.5} /> Cancel</button>
                  </div>
                </div>
              ) : (
                (r.caption || r.script) && (
                  <div className="flex items-start gap-2">
                    <p className="text-sm text-slate-400 leading-relaxed line-clamp-4 flex-1">“{r.caption || r.script}”</p>
                    {r.status !== "submitted" && (
                      <button
                        data-testid={`reel-caption-btn-${r.id}`}
                        onClick={() => startEdit(r)}
                        className="shrink-0 text-slate-500 hover:text-[#00E5FF] transition-colors"
                        aria-label="Edit caption"
                      ><Pencil className="w-4 h-4" strokeWidth={1.5} /></button>
                    )}
                  </div>
                )
              ))}

              <div className="flex items-center gap-1.5 flex-wrap text-xs text-slate-500">
                {(r.platforms || []).map((p) => <span key={p} className="px-2 py-0.5 rounded-full bg-[#141B29]">{p}</span>)}
              </div>
              {r.status_detail && r.status !== "failed" && <p className="text-xs text-slate-500">{r.status_detail}</p>}

              <div className="flex items-center gap-2 pt-1">
                {r.status === "draft" && (
                  <>
                    <button
                      data-testid={`reel-generate-video-${r.id}`}
                      onClick={() => render(r.id)}
                      disabled={busyId === r.id}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-sm px-4 py-1.5 transition-colors disabled:opacity-60"
                    >{busyId === r.id ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <Sparkles className="w-4 h-4" strokeWidth={1.5} />} Generate video</button>
                    {draftEdit[r.id] && (
                      <button
                        data-testid={`reel-draft-save-${r.id}`}
                        onClick={() => saveDraft(r.id)}
                        disabled={busyId === r.id}
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#0E131F] border border-[#29344D] hover:border-[#00E5FF] text-slate-400 text-sm px-4 py-1.5 transition-colors disabled:opacity-60"
                      ><Check className="w-4 h-4" strokeWidth={1.5} /> Save</button>
                    )}
                  </>
                )}
                {r.video_url && r.status !== "submitted" && (
                  <button
                    data-testid={`reel-approve-${r.id}`}
                    onClick={() => approve(r.id)}
                    disabled={busyId === r.id}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#0E131F] border border-[#29344D] hover:border-[#00E5FF] text-slate-400 text-sm px-4 py-1.5 transition-colors disabled:opacity-60"
                  ><Send className="w-4 h-4" strokeWidth={1.5} /> PostPulse</button>
                )}
                {r.video_url && r.status !== "submitted" && (
                  <button
                    data-testid={`reel-post-fb-${r.id}`}
                    onClick={() => postFacebook(r.id)}
                    disabled={busyId === r.id}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#1877F2] hover:bg-[#1568d8] text-white text-sm px-4 py-1.5 transition-colors disabled:opacity-60"
                  ><Facebook className="w-4 h-4" strokeWidth={1.5} /> Post to Facebook</button>
                )}
                <button
                  data-testid={`reel-delete-${r.id}`}
                  onClick={() => remove(r.id)}
                  disabled={busyId === r.id}
                  className="ml-auto inline-flex items-center justify-center w-9 h-9 rounded-full text-slate-500 hover:text-red-500 hover:bg-red-500/10 transition-colors"
                  aria-label="Delete reel"
                ><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {archivedR.length > 0 && (
        <div className="border border-dashed border-[#29344D] rounded-2xl bg-[#0B0F16]/40" data-testid="reel-archive">
          <button data-testid="reel-archive-toggle" onClick={() => setShowArchive((v) => !v)}
            className="w-full flex items-center gap-3 p-4 text-left">
            <span className="w-8 h-8 rounded-lg bg-[#141B29] text-slate-500 flex items-center justify-center shrink-0"><Archive className="w-4 h-4" strokeWidth={1.7} /></span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-slate-400">Archived · older than 7 days</span>
              <span className="block text-xs text-slate-500">{archivedR.length} posted — hidden to reduce clutter, still here to reuse</span>
            </span>
            <ChevronRight className={`ml-auto w-4 h-4 text-slate-500 transition-transform ${showArchive ? "rotate-90" : ""}`} />
          </button>
          {showArchive && (
            <div className="border-t border-dashed border-[#29344D] p-2 space-y-1">
              {archivedR.map((r) => (
                <div key={r.id} data-testid={`reel-arch-${r.id}`} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#0E131F] transition-colors">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-300 truncate">{r.title || r.brief}</div>
                    <div className="text-xs text-slate-500">{(r.reel_type || "broll")} · {fmtDate(r.rendered_at || r.created_at)}</div>
                  </div>
                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full shrink-0 ${STATUS_STYLES[r.status] || STATUS_STYLES.generating}`}>{r.status}</span>
                  <button data-testid={`reel-arch-reuse-${r.id}`} onClick={() => reuseBrief(r)}
                    className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-[#29344D] text-slate-400 hover:bg-[#0B0F16] text-xs px-3 py-1.5">
                    <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} /> Reuse
                  </button>
                  <button data-testid={`reel-arch-del-${r.id}`} onClick={() => remove(r.id)} disabled={busyId === r.id}
                    className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full text-slate-500 hover:text-red-500 hover:bg-red-500/10" aria-label="Delete reel">
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
